import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, SITE_URL, parsePersonId, personPath } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { MovieCard } from "@/components/MovieCard";
import { AdSlot } from "@/components/AdSlot";
import { useLang } from "@/context/LanguageContext";

export default function PersonDetail() {
  const { id: param } = useParams();
  const id = parsePersonId(param);
  const { t } = useLang();
  const [person, setPerson] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/person/${id}`);
      setPerson(data);
      window.scrollTo(0, 0);
    } catch { setNotFound(true); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505]"><Navbar />
        <div className="pt-32 text-center text-zinc-400"><p>{t("personNotFound")}</p><Link to="/" className="text-[#E11D48] mt-4 inline-block">{t("back")}</Link></div>
      </div>
    );
  }
  if (!person) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-20" data-testid="person-detail">
      <Helmet>
        <title>{`${person.name} — Filmografía LGBTQ+ | La Caja LGBT`}</title>
        <meta name="description" content={(person.biography || `Filmografía de ${person.name} en La Caja LGBT`).slice(0, 160)} />
        <link rel="canonical" href={`${SITE_URL}${personPath(person.id, person.name)}`} />
      </Helmet>
      <Navbar />
      <div className="pt-28 px-6 md:px-16">
        <div className="flex flex-col md:flex-row gap-8">
          <img src={person.profile_url || ""} alt={person.name} className="w-40 md:w-56 aspect-[2/3] object-cover rounded-sm border border-white/10 bg-zinc-900 shrink-0" />
          <div className="flex-1">
            <h1 className="font-heading text-3xl md:text-5xl font-light tracking-tighter">{person.name}</h1>
            <div className="flex flex-wrap gap-3 text-sm text-zinc-400 mt-3">
              {person.known_for_department && <span className="uppercase text-xs border border-white/15 px-2 py-0.5">{person.known_for_department === "Directing" ? "Dirección" : person.known_for_department === "Acting" ? "Actuación" : person.known_for_department}</span>}
              {person.birthday && <span>{t("birth")}: {person.birthday}</span>}
              {person.place_of_birth && <span>· {person.place_of_birth}</span>}
            </div>
            {person.biography && <p className="text-zinc-300 leading-relaxed mt-6 max-w-3xl line-clamp-6">{person.biography}</p>}
          </div>
        </div>

        <div className="my-10"><AdSlot slot="9313551683" /></div>

        <h2 className="font-heading text-2xl mb-4">{t("inOurCatalog")}</h2>
        {person.titles.length === 0 ? (
          <p className="text-zinc-400">{t("noTitles")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
            {person.titles.map((m) => <MovieCard key={m.slug} movie={{ ...m, id: m.slug }} className="w-full" />)}
          </div>
        )}
      </div>
    </div>
  );
}
